import { CustomDimensionsOptions } from 'src/shared/helpers/analyticsHelper';
import loadGoogleAnalytics from './loadGoogleAnalytics';

type TrackPageView = {
    page_title?: string;
    page_location?: string;
    page_path: string;
};

export type TrackEventParams = CustomDimensionsOptions & {
    event_action: string;
    event_category: string;
    event_label?: string;
    non_interaction?: boolean;
    value?: number;
};

// GA4 uses "(not set)" in reports for missing values; mirror that convention so
// unknown dimensions read consistently with the rest of Google Analytics.
export const NOT_SET_DIMENSION = 'not set';

export enum TrafficUserType {
    INTERNAL = 'internal',
    EXTERNAL = 'external',
    NOT_SET = NOT_SET_DIMENSION,
}

export type GtagConfig = {
    traffic_user_type: TrafficUserType;
    customer_name: string;
    customer_id: string;
    project: string;
    project_id: string;
    debug_mode?: boolean;
};

export const defaultGtagConfig: GtagConfig = {
    traffic_user_type: TrafficUserType.NOT_SET,
    customer_name: NOT_SET_DIMENSION,
    customer_id: NOT_SET_DIMENSION,
    project: NOT_SET_DIMENSION,
    project_id: NOT_SET_DIMENSION,
};

// One boolean per Role. Sent as separate GA4 user properties (rather than
// a joined string) so each role filters independently in the GA UI without
// substring matches, and so we don't hit the 36-char user-property value
// cap. Values are always set explicitly on every setUserProperties call —
// never omitted — so an absent role reads as false, not as stale state
// from a prior session.
export type GtagUserRoleFlags = {
    role_admin: boolean;
    role_customer_admin: boolean;
    role_nm_customer_manager: boolean;
    role_project_admin: boolean;
    role_nm_project_admin: boolean;
    role_project_viewer: boolean;
};

export const defaultGtagUserRoleFlags: GtagUserRoleFlags = {
    role_admin: false,
    role_customer_admin: false,
    role_nm_customer_manager: false,
    role_project_admin: false,
    role_nm_project_admin: false,
    role_project_viewer: false,
};

// User properties describe *who the user is* (persist across events until
// changed), separate from event params which describe the event itself.
// GA4 docs: https://support.google.com/analytics/answer/9268042
export type GtagUserProperties = GtagUserRoleFlags & {
    // Second, independent internal-user signal. Kept separate from
    // traffic_user_type so we can cross-check the two: disagreement
    // between them flags a misconfigured is_internal_user API flag.
    email_domain_internal_user: TrafficUserType;
};

export const defaultGtagUserProperties: GtagUserProperties = {
    ...defaultGtagUserRoleFlags,
    email_domain_internal_user: TrafficUserType.NOT_SET,
};

/**
 * The interface of the GoogleAnalytics class below
 * replicates the interface of the ReactGA library
 */
class GoogleAnalytics {
    static initialize(googleAnalyticsKey: string) {
        loadGoogleAnalytics(googleAnalyticsKey);
    }

    // reference: example from Google's docs for gtag.js
    // https://developers.google.com/analytics/devguides/collection/gtagjs/pages
    static pageview(params: TrackPageView) {
        window.gtag('event', 'page_view', params);
    }

    // reference: Measure Google Analytics Events gtag.js
    // https://developers.google.com/analytics/devguides/collection/gtagjs/events
    static event(eventParams: TrackEventParams) {
        window.gtag('event', eventParams.event_action, eventParams);
    }

    static updateConfig(googleAnalyticsKey: string, newConfig: GtagConfig) {
        window.gtag('config', googleAnalyticsKey, newConfig);
    }

    // reference: GA4 user_properties
    // https://developers.google.com/analytics/devguides/collection/ga4/user-properties
    static setUserProperties(properties: GtagUserProperties) {
        window.gtag('set', 'user_properties', properties);
    }

    // Persistent, cross-device identifier for a signed-in user. Must be a
    // pseudonymous value (GA4 rejects raw PII). Sent via gtag('set') so it
    // applies to all subsequent events without triggering a page_view.
    // https://developers.google.com/analytics/devguides/collection/ga4/user-id
    static setUserId(userId: string) {
        window.gtag('set', { user_id: userId });
    }
}

export default GoogleAnalytics;
